/**
 * Power Pages management API client (Tier 2 — control plane).
 *
 * Talks to the Power Platform API "powerpages" namespace:
 *   https://api.powerplatform.com/powerpages/environments/{environmentId}/websites
 *
 * This is the site LIFECYCLE/hosting plane (list/get/create/delete/restart sites),
 * NOT the Dataverse config plane. The Dataverse config (pages, web roles, content
 * snippets, etc.) is reached through DataverseApi via the powerpages-tools.
 *
 * Auth: a DELEGATED user token for the https://api.powerplatform.com audience
 * (scope https://api.powerplatform.com/.default). The signed-in user must hold a
 * Power Pages / Power Platform admin role. This audience requires the "Power
 * Platform API" delegated permission on the app registration — if it has not been
 * consented, token acquisition fails with a clear "re-run --setup" message.
 */

import { requestWithRetry } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import { createChildLogger } from "../utils/logger.js";
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
  wrapApiCall,
} from "../utils/errors.js";
import { POWER_PAGES_API_BASE, POWER_PAGES_API_VERSION } from "../utils/constants.js";
import { validateEnvironmentId, validateRowId, sanitizeErrorMessage } from "../utils/security.js";

const apiLogger = createChildLogger({ module: "powerpages-api" });

export interface PowerPagesApiConfig {
  authProvider: AuthProvider;
  defaultEnvironmentId: string;
}

/** Website template names accepted by the Power Pages 2024-10-01 management API. */
export type PowerPagesTemplateName =
  "DefaultPortalTemplate" | "PowerPortals_ProgramRegistration" | "PowerPortals_BookMeeting";

/**
 * A Power Pages website as returned by the management API (WebsiteDto).
 * Only the fields we surface are typed; the API returns more.
 */
export interface PowerPagesWebsite {
  id?: string;
  name?: string;
  websiteUrl?: string;
  subdomain?: string;
  status?: string;
  type?: string;
  dataModel?: string;
  dataverseInstanceUrl?: string;
  dataverseOrganizationId?: string;
  websiteRecordId?: string;
  templateName?: string;
  selectedBaseLanguage?: number;
  packageInstallStatus?: string;
  trialExpiringInDays?: number;
  [key: string]: unknown;
}

export interface CreateWebsiteOptions {
  name: string;
  subdomain: string;
  /** LCID of the base language, e.g. 1033 for English (US). */
  selectedBaseLanguage?: number;
  /** Exact website template name from the Power Pages 2024-10-01 contract. */
  templateName?: PowerPagesTemplateName;
  /** Dataverse org id (GUID) the site is backed by. Required by the API. */
  dataverseOrganizationId: string;
  /** Optional existing website (adx_website/powerpagesite) record id to bind to. */
  websiteRecordId?: string;
}

export interface CreateWebsiteResult {
  /** Async operation tracking URL from the Operation-Location response header. */
  operationLocation?: string;
  /** Response body (often empty on a 202). */
  body: PowerPagesWebsite | Record<string, unknown>;
  statusCode: number;
}

/** Common result shape for management operations, including asynchronous ones. */
export interface PowerPagesOperationResult<T = Record<string, unknown>> {
  /** Async operation tracking URL returned by the service, when applicable. */
  operationLocation?: string;
  body: T;
  statusCode: number;
}

export interface PowerPagesOperationStatus {
  id?: string;
  name?: string;
  status?: string;
  state?: string;
  error?: unknown;
  [key: string]: unknown;
}

export interface PowerPagesIpAddress {
  IpAddress: string;
  IpAddressType: "IPv4" | "IPv6";
}

export interface PowerPagesCertificate {
  ExpirationDate?: string;
  Location?: string;
  SubjectName?: string;
  Thumbprint?: string;
  Type?: string;
  [key: string]: unknown;
}

export interface PowerPagesSslBinding {
  Name?: string;
  Thumbprint?: string;
  [key: string]: unknown;
}

export type WafRuleAction = "Allow" | "Block" | "Log" | "AnomalyScoring";
export type WafRuleEnabledState = "Disabled" | "Enabled";
export type WafRuleType = "MatchRule" | "RateLimitRule";

export interface WafMatchCondition {
  matchValue: string[];
  matchVariable: string;
  negateCondition?: boolean;
  operator: "Contains" | "EndsWith" | "Equals" | "GeoMatch" | "StartsWith";
  selector?: string;
  transforms?: string[];
}

export interface WafCustomRule {
  action: Exclude<WafRuleAction, "AnomalyScoring">;
  enabledState: WafRuleEnabledState;
  matchConditions: WafMatchCondition[];
  name: string;
  priority: number;
  rateLimitDurationInMinutes?: number;
  rateLimitThreshold?: number;
  ruleType: WafRuleType;
}

export interface WafManagedRuleOverride {
  Action: WafRuleAction;
  EnabledState: WafRuleEnabledState;
  Exclusions?: string[];
  RuleId: string;
}

export interface WafRuleGroupOverride {
  Exclusions?: string[];
  RuleGroupName: string;
  Rules?: WafManagedRuleOverride[];
}

export interface WafManagedRuleSet {
  Exclusions?: string[];
  RuleGroupOverrides?: WafRuleGroupOverride[];
  RuleSetAction: Exclude<WafRuleAction, "AnomalyScoring">;
  RuleSetType: string;
  RuleSetVersion: string;
}

export interface PowerPagesWafRules {
  customRules?: WafCustomRule[];
  managedRules?: WafManagedRuleSet[];
  /** GET uses PascalCase in the published response contract. */
  CustomRules?: WafCustomRule[];
  ManagedRules?: WafManagedRuleSet[];
}

export interface SiteSecurityScore {
  succeededRules?: number;
  totalRules?: number;
  [key: string]: unknown;
}

export interface SiteSecurityReport {
  StartTime?: string;
  EndTime?: string;
  FailedRuleCount?: number;
  TotalAlertCount?: number;
  TotalRuleCount?: number;
  UserName?: string;
  Rules?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface UploadCertificateOptions {
  certType: "SSL" | "MANAGED";
  /** Raw PFX bytes. */
  file: Buffer;
  fileName?: string;
  /** PFX password. Use an empty string for a passwordless PFX. */
  password: string;
}

export interface WaitForOperationOptions {
  /** Maximum time spent polling. Default 60 seconds; maximum 10 minutes. */
  timeoutMs?: number;
  /** Delay between polls. Default 2 seconds; minimum 250ms. */
  intervalMs?: number;
  /** Injectable clock delay for deterministic tests. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Validate a Power Pages subdomain: lowercase letters, digits, and hyphens,
 * not starting/ending with a hyphen. Mirrors the platform's subdomain rules.
 */
function validateSubdomain(subdomain: string): string {
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(subdomain)) {
    throw new ApiError(
      "Invalid subdomain: use lowercase letters, digits, and hyphens (not leading/trailing)",
      400,
    );
  }
  return subdomain;
}

function validateHostname(hostname: string): string {
  const normalized = hostname.trim().toLowerCase();
  if (
    normalized.length > 253 ||
    !/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(normalized)
  ) {
    throw new ApiError("Invalid custom-domain hostname", 400);
  }
  return normalized;
}

function validateThumbprint(thumbprint: string): string {
  const normalized = thumbprint.replace(/\s/g, "").toUpperCase();
  if (!/^[A-F0-9]{40,128}$/.test(normalized)) {
    throw new ApiError("Invalid certificate thumbprint", 400);
  }
  return normalized;
}

function validateOperationLocation(operationLocation: string): string {
  let url: URL;
  try {
    url = new URL(operationLocation);
  } catch {
    throw new ApiError("Invalid Power Pages operation URL", 400);
  }
  const expectedOrigin = new URL(POWER_PAGES_API_BASE).origin;
  if (url.protocol !== "https:" || url.origin !== expectedOrigin || url.username || url.password) {
    throw new ApiError(
      `Power Pages operation URL must use ${expectedOrigin}; refusing to send credentials elsewhere`,
      400,
    );
  }
  return url.toString();
}

function operationIsTerminal(status: PowerPagesOperationStatus): boolean {
  const value = String(status.status ?? status.state ?? "").toLowerCase();
  return [
    "succeeded",
    "success",
    "completed",
    "operationcomplete",
    "failed",
    "operationfailed",
    "cancelled",
    "canceled",
  ].includes(value);
}

/**
 * Client for the Power Pages management API (api.powerplatform.com/powerpages).
 */
export class PowerPagesApi {
  private readonly authProvider: AuthProvider;
  private readonly defaultEnvironmentId: string;

  constructor(config: PowerPagesApiConfig) {
    this.authProvider = config.authProvider;
    this.defaultEnvironmentId = config.defaultEnvironmentId;
  }

  private async getAuthHeaders(contentType = "application/json"): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM_API);
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
    if (contentType) headers["Content-Type"] = contentType;
    return headers;
  }

  private buildUrl(
    environmentId: string,
    suffix = "",
    query: Record<string, string | number | boolean | undefined> = {},
  ): string {
    const base = `${POWER_PAGES_API_BASE}/powerpages/environments/${environmentId}/websites${suffix}`;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.set(key, String(value));
    }
    params.set("api-version", POWER_PAGES_API_VERSION);
    return `${base}?${params.toString()}`;
  }

  private async requestWebsite<T>(
    context: string,
    options: {
      environmentId?: string;
      websiteId?: string;
      suffix?: string;
      method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
      query?: Record<string, string | number | boolean | undefined>;
      body?: unknown;
      headers?: Record<string, string>;
    },
  ): Promise<PowerPagesOperationResult<T>> {
    const envId = validateEnvironmentId(options.environmentId ?? this.defaultEnvironmentId);
    const websitePath = options.websiteId ? `/${validateRowId(options.websiteId)}` : "";
    const url = this.buildUrl(envId, `${websitePath}${options.suffix ?? ""}`, options.query);
    const headers = options.headers ?? (await this.getAuthHeaders());
    const method = options.method ?? "GET";
    const requestOptions: Parameters<typeof requestWithRetry>[1] = { method, headers };
    if (options.body !== undefined) {
      requestOptions.body =
        typeof options.body === "string" ||
        Buffer.isBuffer(options.body) ||
        options.body instanceof FormData
          ? options.body
          : JSON.stringify(options.body);
    }

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, requestOptions);
      const operationLocation = response.headers["operation-location"];
      const body = await this.handleResponse<T>(response, context);
      return {
        operationLocation: operationLocation
          ? validateOperationLocation(String(operationLocation))
          : undefined,
        body,
        statusCode: response.statusCode,
      };
    }, context);
  }

  private async runWebsiteAction<T = Record<string, unknown>>(
    websiteId: string,
    suffix: string,
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    context: string,
    options: {
      environmentId?: string;
      query?: Record<string, string | number | boolean | undefined>;
      body?: unknown;
      headers?: Record<string, string>;
    } = {},
  ): Promise<PowerPagesOperationResult<T>> {
    return this.requestWebsite<T>(context, {
      ...options,
      websiteId,
      suffix,
      method,
    });
  }

  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string,
  ): Promise<T> {
    const { statusCode, body } = response;

    if (statusCode === 429) {
      try {
        await body.text();
      } catch {
        body.destroy();
      }
      const retryAfter = response.headers["retry-after"];
      const seconds = retryAfter ? parseInt(String(retryAfter), 10) : NaN;
      throw new RateLimitError(Number.isNaN(seconds) ? undefined : seconds * 1000);
    }

    // Stream-read with a size cap (defensive — these responses are small).
    const MAX_RESPONSE_SIZE = 16 * 1024 * 1024; // 16MB
    const chunks: Buffer[] = [];
    let totalSize = 0;
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      totalSize += chunk.length;
      if (totalSize > MAX_RESPONSE_SIZE) {
        body.destroy();
        throw new ApiError(`${context}: response too large (max 16MB)`, 413);
      }
      chunks.push(Buffer.from(chunk));
    }
    const responseText = Buffer.concat(chunks).toString("utf-8");

    if (statusCode === 401) {
      // Power Pages reports missing USER permissions as 401 with a JSON body
      // (ErrorCode D004: "User doesn't have required permissions"), not 403 —
      // observed live 2026-08-07. Reporting that as "Authentication failed"
      // sends users to sign_in when the actual fix is a role grant.
      let detail = "";
      try {
        const parsed = JSON.parse(responseText) as { Message?: string; ErrorCode?: string };
        if (parsed.Message) {
          detail = `${parsed.Message}${parsed.ErrorCode ? ` (${parsed.ErrorCode})` : ""}`;
        }
      } catch {
        // not JSON — keep the generic auth error below
      }
      if (/permission/i.test(detail)) {
        throw new AuthorizationError(
          `${context}: ${sanitizeErrorMessage(detail)} — your signed-in account needs a ` +
            `Power Pages admin role (e.g. System Administrator) in this environment. ` +
            `Signing in again will not fix this; an environment admin has to grant the role.`,
        );
      }
      throw new AuthenticationError(
        `${context}: Authentication failed${detail ? ` — ${sanitizeErrorMessage(detail)}` : ""}`,
      );
    }
    if (statusCode === 403) {
      apiLogger.error(
        { statusCode, errorBody: sanitizeErrorMessage(responseText), context },
        "Access denied",
      );
      throw new AuthorizationError(
        `${context}: requires a Power Pages / Power Platform admin role for this environment`,
      );
    }
    if (statusCode >= 400) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = responseText;
      }
      // Sanitize before surfacing — error bodies can carry GUIDs / emails / instance URLs.
      const safeDetails = sanitizeErrorMessage(
        typeof parsed === "string" ? parsed : JSON.stringify(parsed),
      );
      throw new ApiError(`${context}: API error`, statusCode, safeDetails);
    }

    if (!responseText) {
      return {} as T;
    }
    try {
      return JSON.parse(responseText) as T;
    } catch (e) {
      throw new ApiError(`${context}: invalid JSON response (status ${statusCode})`, statusCode, {
        parseError: String(e),
      });
    }
  }

  // ==========================================================================
  // Website lifecycle
  // ==========================================================================

  /** List every Power Pages website in an environment, following @odata.nextLink pages. */
  async listWebsites(
    environmentId?: string,
    options: { maxPages?: number; maxItems?: number } = {},
  ): Promise<PowerPagesWebsite[]> {
    const envId = validateEnvironmentId(environmentId ?? this.defaultEnvironmentId);
    apiLogger.debug({ envId }, "Listing Power Pages websites");

    let url: string | undefined = this.buildUrl(envId);
    const headers = await this.getAuthHeaders();
    const maxPages = Math.min(Math.max(options.maxPages ?? 100, 1), 1_000);
    const maxItems = Math.min(Math.max(options.maxItems ?? 10_000, 1), 100_000);

    return wrapApiCall(async () => {
      const list: PowerPagesWebsite[] = [];
      const expectedPath = `/powerpages/environments/${envId}/websites`;
      for (let page = 0; url && page < maxPages && list.length < maxItems; page += 1) {
        const response = await requestWithRetry(url, { method: "GET", headers });
        const data = await this.handleResponse<
          | {
              value?: PowerPagesWebsite[];
              "@odata.nextLink"?: string;
              nextLink?: string;
            }
          | PowerPagesWebsite[]
        >(response, `List Power Pages websites in ${envId}`);

        if (Array.isArray(data)) {
          list.push(...data.slice(0, maxItems - list.length));
          url = undefined;
          break;
        }

        list.push(...(data.value ?? []).slice(0, maxItems - list.length));
        const nextLink = data["@odata.nextLink"] ?? data.nextLink;
        if (!nextLink) {
          url = undefined;
          break;
        }
        const safeNext = validateOperationLocation(nextLink);
        if (!new URL(safeNext).pathname.startsWith(expectedPath)) {
          throw new ApiError("Power Pages pagination URL escaped the website collection", 400);
        }
        url = safeNext;
      }
      if (url && list.length < maxItems) {
        throw new ApiError(`List Power Pages websites exceeded ${maxPages} pages`, 502);
      }
      apiLogger.info({ count: list.length }, "Listed Power Pages websites");
      return list;
    }, `List Power Pages websites in ${envId}`);
  }

  /** Get a single Power Pages website by id. */
  async getWebsite(websiteId: string, environmentId?: string): Promise<PowerPagesWebsite> {
    const envId = validateEnvironmentId(environmentId ?? this.defaultEnvironmentId);
    const safeId = validateRowId(websiteId);
    apiLogger.debug({ envId, websiteId: safeId }, "Getting Power Pages website");

    const url = this.buildUrl(envId, `/${safeId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      return this.handleResponse<PowerPagesWebsite>(response, `Get Power Pages website ${safeId}`);
    }, `Get Power Pages website ${safeId}`);
  }

  /**
   * Create (provision) a new Power Pages website. Asynchronous: returns 202 with an
   * Operation-Location header to poll. The site takes minutes to provision.
   */
  async createWebsite(
    options: CreateWebsiteOptions,
    environmentId?: string,
  ): Promise<CreateWebsiteResult> {
    const subdomain = validateSubdomain(options.subdomain);
    const dataverseOrganizationId = validateRowId(options.dataverseOrganizationId);
    apiLogger.info({ name: options.name, subdomain }, "Creating Power Pages website");

    const requestBody: Record<string, unknown> = {
      name: options.name,
      subdomain,
      templateName: options.templateName ?? "DefaultPortalTemplate",
      selectedBaseLanguage: options.selectedBaseLanguage ?? 1033,
      dataverseOrganizationId,
    };
    if (options.websiteRecordId) {
      requestBody.websiteRecordId = validateRowId(options.websiteRecordId);
    }

    return this.requestWebsite<PowerPagesWebsite | Record<string, unknown>>(
      `Create Power Pages website "${options.name}"`,
      { environmentId, method: "POST", body: requestBody },
    );
  }

  /** Delete a Power Pages website. Asynchronous (202 + Operation-Location header). */
  async deleteWebsite(
    websiteId: string,
    environmentId?: string,
  ): Promise<{ operationLocation?: string; statusCode: number }> {
    const safeId = validateRowId(websiteId);
    apiLogger.info({ websiteId: safeId }, "Deleting Power Pages website");
    const result = await this.requestWebsite<Record<string, unknown>>(
      `Delete Power Pages website ${safeId}`,
      { environmentId, websiteId: safeId, method: "DELETE" },
    );
    return { operationLocation: result.operationLocation, statusCode: result.statusCode };
  }

  /** Restart a Power Pages website (recycles the site; picks up config changes). */
  async restartWebsite(websiteId: string, environmentId?: string): Promise<void> {
    const safeId = validateRowId(websiteId);
    apiLogger.info({ websiteId: safeId }, "Restarting Power Pages website");
    await this.runWebsiteAction(
      safeId,
      "/restart",
      "POST",
      `Restart Power Pages website ${safeId}`,
      {
        environmentId,
        body: {},
      },
    );
  }

  // ==========================================================================
  // Long-running operation tracking
  // ==========================================================================

  /** Poll an Operation-Location URL with a fresh Power Platform API token. */
  async getOperationStatus(operationLocation: string): Promise<PowerPagesOperationStatus> {
    // The service's Operation-Location header omits api-version, and the API
    // now rejects unversioned polls ("ApiVersionInvalid: API version must be
    // specified", field report 2026-08-07) — so provisioning succeeded while
    // every poll, and therefore the create tool, reported failure. Append our
    // version when the header didn't carry one; honor it when it did.
    const polled = new URL(validateOperationLocation(operationLocation));
    if (!polled.searchParams.has("api-version")) {
      polled.searchParams.set("api-version", POWER_PAGES_API_VERSION);
    }
    const url = polled.toString();
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      return this.handleResponse<PowerPagesOperationStatus>(response, "Get Power Pages operation");
    }, "Get Power Pages operation");
  }

  /** Poll until the service reports a terminal state or the bounded timeout elapses. */
  async waitForOperation(
    operationLocation: string,
    options: WaitForOperationOptions = {},
  ): Promise<PowerPagesOperationStatus> {
    const timeoutMs = Math.min(Math.max(options.timeoutMs ?? 60_000, 1_000), 600_000);
    const intervalMs = Math.min(Math.max(options.intervalMs ?? 2_000, 250), 30_000);
    const sleep =
      options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    const started = Date.now();
    let latest: PowerPagesOperationStatus = {};

    while (Date.now() - started <= timeoutMs) {
      latest = await this.getOperationStatus(operationLocation);
      if (operationIsTerminal(latest)) return latest;
      const remaining = timeoutMs - (Date.now() - started);
      if (remaining <= 0) break;
      await sleep(Math.min(intervalMs, remaining));
    }
    throw new ApiError(`Power Pages operation did not complete within ${timeoutMs}ms`, 408, {
      latestStatus: latest.status ?? latest.state ?? "unknown",
    });
  }

  // ==========================================================================
  // IP allow list
  // ==========================================================================

  async getAllowedIpAddresses(
    websiteId: string,
    environmentId?: string,
  ): Promise<{ IpAddresses?: PowerPagesIpAddress[]; [key: string]: unknown }> {
    const result = await this.runWebsiteAction<{
      IpAddresses?: PowerPagesIpAddress[];
      [key: string]: unknown;
    }>(websiteId, "/ipaddressrules", "GET", "Get Power Pages allowed IP addresses", {
      environmentId,
    });
    return result.body;
  }

  async addAllowedIpAddresses(
    websiteId: string,
    ipAddresses: PowerPagesIpAddress[],
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/ipaddressrules",
      "POST",
      "Add Power Pages allowed IP addresses",
      { environmentId, body: { IpAddresses: ipAddresses } },
    );
  }

  async removeAllowedIpAddresses(
    websiteId: string,
    ipAddresses: PowerPagesIpAddress[],
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/removeAllowedIpAddresses",
      "POST",
      "Remove Power Pages allowed IP addresses",
      { environmentId, body: { IpAddresses: ipAddresses } },
    );
  }

  // ==========================================================================
  // Host names, certificates, and SSL bindings
  // ==========================================================================

  async listCustomDomains(websiteId: string, environmentId?: string): Promise<string[]> {
    const result = await this.runWebsiteAction<string[] | { value?: string[] }>(
      websiteId,
      "/customDomain",
      "GET",
      "List Power Pages custom domains",
      { environmentId },
    );
    return Array.isArray(result.body) ? result.body : (result.body.value ?? []);
  }

  async createCustomDomain(
    websiteId: string,
    customDomain: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/customDomain",
      "POST",
      "Create Power Pages custom domain",
      { environmentId, query: { customDomain: validateHostname(customDomain) } },
    );
  }

  async deleteCustomDomain(
    websiteId: string,
    hostName: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/customDomain",
      "DELETE",
      "Delete Power Pages custom domain",
      { environmentId, query: { hostName: validateHostname(hostName) } },
    );
  }

  async getCertificates(
    websiteId: string,
    certType: "SSL" | "MANAGED",
    environmentId?: string,
  ): Promise<PowerPagesCertificate[]> {
    const result = await this.runWebsiteAction<
      PowerPagesCertificate[] | { value?: PowerPagesCertificate[] }
    >(websiteId, "/certificates", "GET", "Get Power Pages certificates", {
      environmentId,
      query: { certType },
    });
    return Array.isArray(result.body) ? result.body : (result.body.value ?? []);
  }

  async uploadCertificate(
    websiteId: string,
    options: UploadCertificateOptions,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    if (options.file.length === 0) throw new ApiError("Certificate file is empty", 400);
    if (options.file.length > 16 * 1024 * 1024) {
      throw new ApiError("Certificate file exceeds the 16 MiB safety limit", 413);
    }
    const form = new FormData();
    form.append(
      "file",
      new Blob([Uint8Array.from(options.file)], { type: "application/x-pkcs12" }),
      options.fileName ?? "certificate.pfx",
    );
    form.append("password", options.password);
    const headers = await this.getAuthHeaders("");
    return this.runWebsiteAction(
      websiteId,
      "/certificates",
      "POST",
      "Upload Power Pages certificate",
      {
        environmentId,
        query: { certType: options.certType },
        body: form,
        headers,
      },
    );
  }

  async deleteCertificate(
    websiteId: string,
    thumbprint: string,
    certType: "SSL" | "MANAGED",
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/certificates",
      "DELETE",
      "Delete Power Pages certificate",
      {
        environmentId,
        query: { thumbprint: validateThumbprint(thumbprint), certType },
      },
    );
  }

  async listSslBindings(
    websiteId: string,
    hostName: string,
    environmentId?: string,
  ): Promise<PowerPagesSslBinding[]> {
    const result = await this.runWebsiteAction<
      PowerPagesSslBinding[] | { value?: PowerPagesSslBinding[] }
    >(websiteId, "/sslBindings", "GET", "List Power Pages SSL bindings", {
      environmentId,
      query: { hostName: validateHostname(hostName) },
    });
    return Array.isArray(result.body) ? result.body : (result.body.value ?? []);
  }

  async addSslBinding(
    websiteId: string,
    thumbprint: string,
    hostName: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(websiteId, "/sslBindings", "POST", "Add Power Pages SSL binding", {
      environmentId,
      query: {
        thumbprint: validateThumbprint(thumbprint),
        hostName: validateHostname(hostName),
      },
    });
  }

  async deleteSslBinding(
    websiteId: string,
    hostName: string,
    thumbprint: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/sslBindings",
      "DELETE",
      "Delete Power Pages SSL binding",
      {
        environmentId,
        query: {
          hostName: validateHostname(hostName),
          thumbprint: validateThumbprint(thumbprint),
        },
      },
    );
  }

  // ==========================================================================
  // Web application firewall
  // ==========================================================================

  async getWafStatus(websiteId: string, environmentId?: string): Promise<unknown> {
    const result = await this.runWebsiteAction<unknown>(
      websiteId,
      "/getWafStatus",
      "GET",
      "Get Power Pages WAF status",
      { environmentId },
    );
    return result.body;
  }

  async getWafRules(
    websiteId: string,
    ruleType?: string,
    environmentId?: string,
  ): Promise<PowerPagesWafRules> {
    const result = await this.runWebsiteAction<PowerPagesWafRules>(
      websiteId,
      "/getWafRules",
      "GET",
      "Get Power Pages WAF rules",
      { environmentId, query: { ruleType } },
    );
    return result.body;
  }

  async enableWaf(websiteId: string, environmentId?: string): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(websiteId, "/enableWaf", "POST", "Enable Power Pages WAF", {
      environmentId,
    });
  }

  async disableWaf(websiteId: string, environmentId?: string): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(websiteId, "/disableWaf", "POST", "Disable Power Pages WAF", {
      environmentId,
    });
  }

  async createWafRules(
    websiteId: string,
    rules: PowerPagesWafRules,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/createWafRules",
      "PUT",
      "Create Power Pages WAF rules",
      {
        environmentId,
        body: {
          customRules: rules.customRules ?? rules.CustomRules ?? [],
          managedRules: rules.managedRules ?? rules.ManagedRules ?? [],
        },
      },
    );
  }

  async deleteWafCustomRules(
    websiteId: string,
    ruleNames: string[],
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/deleteWafCustomRules",
      "PUT",
      "Delete Power Pages WAF custom rules",
      { environmentId, body: ruleNames },
    );
  }

  // ==========================================================================
  // Security scans
  // ==========================================================================

  async startQuickScan(
    websiteId: string,
    lcid?: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/scan/quick/execute",
      "POST",
      "Start Power Pages quick scan",
      { environmentId, query: { lcid } },
    );
  }

  async startDeepScan(
    websiteId: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/scan/deep/start",
      "POST",
      "Start Power Pages deep scan",
      { environmentId },
    );
  }

  async getSecurityScanReport(
    websiteId: string,
    environmentId?: string,
  ): Promise<SiteSecurityReport> {
    const result = await this.runWebsiteAction<SiteSecurityReport>(
      websiteId,
      "/scan/deep/getLatestCompletedReport",
      "GET",
      "Get Power Pages security scan report",
      { environmentId },
    );
    return result.body;
  }

  async getSecurityScanScore(
    websiteId: string,
    environmentId?: string,
  ): Promise<SiteSecurityScore> {
    const result = await this.runWebsiteAction<SiteSecurityScore>(
      websiteId,
      "/scan/deep/getSecurityScore",
      "GET",
      "Get Power Pages security scan score",
      { environmentId },
    );
    return result.body;
  }

  // ==========================================================================
  // Website state and configuration
  // ==========================================================================

  async startWebsite(
    websiteId: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(websiteId, "/start", "POST", "Start Power Pages website", {
      environmentId,
    });
  }

  async stopWebsite(websiteId: string, environmentId?: string): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(websiteId, "/stop", "POST", "Stop Power Pages website", {
      environmentId,
    });
  }

  async convertTrialToProduction(
    websiteId: string,
    options: {
      EnableCDN?: boolean;
      EnableWAF?: boolean;
      UseDynamics365PortalAddOnLicense?: boolean;
    },
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/convertToProduction",
      "POST",
      "Convert Power Pages trial to production",
      {
        environmentId,
        body: {
          EnableCDN: options.EnableCDN ?? false,
          EnableWAF: options.EnableWAF ?? false,
          UseDynamics365PortalAddOnLicense: options.UseDynamics365PortalAddOnLicense ?? false,
        },
      },
    );
  }

  async setBootstrapV5Enabled(
    websiteId: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/SetPortalBootstrapV5Enabled",
      "PATCH",
      "Enable Bootstrap 5 for Power Pages website",
      { environmentId },
    );
  }

  async setDataModelVersion(
    websiteId: string,
    dataModelVersionValue: boolean,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/setPortalDataModelVersion",
      "POST",
      "Set Power Pages data model version",
      { environmentId, body: { dataModelVersionValue } },
    );
  }

  async toggleAfdTrafficRouting(
    websiteId: string,
    enableAfd: boolean,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/toggleAFDTrafficRouting",
      "POST",
      "Toggle Power Pages AFD traffic routing",
      { environmentId, query: { enableAFD: enableAfd } },
    );
  }

  async updateSecurityGroup(
    websiteId: string,
    securityGroupId?: string,
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/updatePortalSecurityGroup",
      "POST",
      "Update Power Pages security group",
      {
        environmentId,
        query: {
          securityGroupId:
            securityGroupId === undefined ? undefined : validateRowId(securityGroupId),
        },
      },
    );
  }

  async updateSiteVisibility(
    websiteId: string,
    siteVisibility?: "public" | "private",
    environmentId?: string,
  ): Promise<PowerPagesOperationResult> {
    return this.runWebsiteAction(
      websiteId,
      "/updateSiteVisibility",
      "POST",
      "Update Power Pages site visibility",
      { environmentId, query: { siteVisibility } },
    );
  }
}
